import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PickCardToReceive as PickCardToReceiveDumb } from '@sorare/core/src/components/cardswap/steps/PickCardToReceive';
import { StepProps } from '@sorare/core/src/components/cardswap/types';
import { useCardSwapContext } from '@sorare/core/src/components/cardswap/useCardSwapContext';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { isType } from '@sorare/core/src/gql';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useToggleArray } from '@sorare/core/src/hooks/useToggleArray';
import { draft } from '@sorare/core/src/lib/glossary';

import { InfoModal } from 'components/cards/InfoModal';
import { CardToReceive } from 'components/cardswap/CardToReceive';

import {
  NBACardSwapOptionsQuery,
  NBACardSwapOptionsQueryVariables,
} from './__generated__/index.graphql';

type NBACardSwapOptionsQuery_currentUser_latestNBACardTrade_userReceiveCardChoices =
  NonNullable<
    NonNullable<NBACardSwapOptionsQuery['currentUser']>['latestNBACardTrade']
  >['userReceiveCardChoices'][number];

const PlayerNameWithScore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--half-unit);
  white-space: nowrap;
  font-weight: var(--t-bold);
`;
const query = gql`
  query NBACardSwapOptionsQuery {
    currentUser {
      slug
      # eslint-disable-next-line @graphql-eslint/no-deprecated
      latestNBACardTrade {
        id
        userGiveCard {
          id
          slug
          anyPlayer {
            slug
            displayName
            ... on NBAPlayer {
              slug
              # eslint-disable-next-line @graphql-eslint/no-deprecated
              tenGameAverageGameStats {
                score
              }
            }
          }
        }
        userReceiveCardChoices {
          id
          ...CardToReceive_NBACommonCardSample
        }
        expiresAfter
        isComplete
      }
    }
  }
  ${CardToReceive.fragments.NBACommonCardSample}
` as TypedDocumentNode<
  NBACardSwapOptionsQuery,
  NBACardSwapOptionsQueryVariables
>;

type Props = StepProps;

export const PickCardToReceive = ({ nextStep }: Props) => {
  const {
    tradeId: cachedTradeId,
    setCardToReceive,
    clearCardSwapState,
  } = useCardSwapContext();
  const { formatMessage } = useIntl();

  const { data } = useQuery(query, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  const [cardsRevealed, setCardsRevealed] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
  ]);
  const setAllCardsRevealed = useToggleArray(setCardsRevealed, 800);

  useEffect(() => {
    setAllCardsRevealed(new Array(cardsRevealed.length).fill(true));
  }, [cardsRevealed.length, setAllCardsRevealed]);

  useEffect(() => {
    if (!data) {
      return;
    }
    const trade = data.currentUser?.latestNBACardTrade;
    const userGiveCard = data.currentUser?.latestNBACardTrade?.userGiveCard;
    const tradeInProgress = trade && !trade.isComplete;
    if (
      !trade ||
      !userGiveCard ||
      !tradeInProgress ||
      trade.id !== cachedTradeId
    ) {
      clearCardSwapState();
    }
  }, [cachedTradeId, clearCardSwapState, data]);

  if (!data) {
    return <LoadingIndicator />;
  }

  const trade = data.currentUser?.latestNBACardTrade;
  const userGiveCard = data.currentUser?.latestNBACardTrade?.userGiveCard;
  const tradeInProgress = trade && !trade.isComplete;
  if (
    !trade ||
    !userGiveCard ||
    !tradeInProgress ||
    trade.id !== cachedTradeId
  ) {
    return null;
  }

  const unsorted = trade.userReceiveCardChoices;
  const cardIds = unsorted.map(({ id }) => id);

  const cardDictionary = unsorted.reduce<
    Record<
      string,
      NBACardSwapOptionsQuery_currentUser_latestNBACardTrade_userReceiveCardChoices
    >
  >((map, card) => {
    map[card.id as string] = card;
    return map;
  }, {});

  const submit = async (optionId: string) => {
    setCardToReceive(optionId);
  };
  const { anyPlayer } = userGiveCard;
  if (!isType(anyPlayer, 'NBAPlayer')) return null;

  return (
    <PickCardToReceiveDumb
      subtitle={formatMessage(
        {
          id: 'Nba.CardSwap.PickCardToReceive.subtitle',
          defaultMessage:
            'Which Card do you want in exchange for {playerNameWithscore}?',
        },
        {
          playerNameWithscore: (
            <PlayerNameWithScore>
              {userGiveCard?.anyPlayer.displayName}
              <Cost
                score={anyPlayer.tenGameAverageGameStats.score}
                title={formatMessage(draft.nbaTenGameAverageScoreTitle, {
                  score: anyPlayer.tenGameAverageGameStats.score,
                })}
              />
            </PlayerNameWithScore>
          ),
        }
      )}
      cardOptions={cardIds}
      renderCard={(
        cardId,
        { isDismissed, isSelected, onSelect, onDisplayPlayerInfo, slotIndex }
      ) => {
        return (
          <CardToReceive
            key={cardId}
            card={cardDictionary[cardId]}
            isDismissed={isDismissed}
            isSelected={isSelected}
            onSelect={onSelect}
            onDisplayPlayerInfo={onDisplayPlayerInfo}
            flipped
            reveal={cardsRevealed[slotIndex]}
          />
        );
      }}
      submit={submit}
      nextStep={nextStep}
      drawerContent={cardId => (
        <InfoModal playerSlug={cardDictionary[cardId]?.player.slug} />
      )}
    />
  );
};
